import React, { Dispatch, SetStateAction, useState } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import ViewShot from 'react-native-view-shot';
import isMobile from '../../../components/IsMobile';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import asNumber from '../../../components/AsNumber';
import WidgetHeader from './WidgetHeader';
import UpArrow12 from '../../../components/12pxIcons/UpArrow12';
import DownArrow12 from '../../../components/12pxIcons/DownArrow12';
import { SolveAnalyticsWidgetProps } from '../Utils';
import PrioritySelector from './PrioritySelector';
import { TicketPriority } from '../../../constants/Types';
import { pad } from '../../LocationManagement/components/BuildingSettingsModal/data';

export interface TimesOnTaskResult {
  average: string,
  slowest: string,
  fastest: string,
  averagePercentageChange: number,
  slowestPercentageChange: number,
  fastestPercentageChange: number,
}

interface TimesOnTaskProps extends SolveAnalyticsWidgetProps {
  selectedTicketPriority: TicketPriority | null,
  setSelectedTicketPriority: Dispatch<SetStateAction<TicketPriority | null>>,
}

const TimesOnTask = ({
  range, compareRange, viewShotRef, data, comparisonData, selectedTicketPriority,
  setSelectedTicketPriority,
}: TimesOnTaskProps) => {
  const [priorityDropdownExpanded, setPriorityDropdownExpanded] = useState<boolean>(false);

  const averageHours: number = asNumber(data?.average?.split(':')[0]);
  const averageMinutes: number = asNumber(data?.average?.split(':')[1]);
  const fastestHours: number = asNumber(data?.fastest?.split(':')[0]);
  const fastestMinutes: number = asNumber(data?.fastest?.split(':')[1]);
  const slowestHours: number = asNumber(data?.slowest?.split(':')[0]);
  const slowestMinutes: number = asNumber(data?.slowest?.split(':')[1]);
  const averageHoursCompared: number = asNumber(comparisonData?.average?.split(':')[0]);
  const averageMinutesCompared: number = asNumber(comparisonData?.average?.split(':')[1]);
  const fastestHoursCompared: number = asNumber(comparisonData?.fastest?.split(':')[0]);
  const fastestMinutesCompared: number = asNumber(comparisonData?.fastest?.split(':')[1]);
  const slowestHoursCompared: number = asNumber(comparisonData?.slowest?.split(':')[0]);
  const slowestMinutesCompared: number = asNumber(comparisonData?.slowest?.split(':')[1]);
  const fastestPercentageChange: number = data?.fastestPercentageChange;
  const slowestPercentageChange: number = data?.slowestPercentageChange;
  const averagePercentageChange: number = data?.averagePercentageChange;

  const comparisonEnabled: boolean = !!(compareRange.start && compareRange.end);

  const PercentageChange = ({ change }: { change: number }) => !!change
    && <View style={styles.percentageChange}>
      {change > 0 && <UpArrow12 />}
      {change < 0 && <DownArrow12 />}
      <Text
        style={[
          styles.title,
          change < 0 && styles.fastestTitle,
          change > 0 && styles.slowestTitle,
        ]}
      >
        {`${Math.abs(change) > 10000 ? '-' : Math.abs(change)}%`}
      </Text>
    </View>;

  return <View
    style={[Style.commonCardStyle, isMobile ? { width: '100%', flex: 3 } : Style.row]}
    testID='solved-in-widget'
  >
    <WidgetHeader
      title='Time on task'
      {...{ range, compareRange }}
      tooltipText='Statistics for time spent on tickets'
      style={{ flex: 1 }}
    />
    <ViewShot
      style={styles.content}
      ref={viewShotRef}
    >
      <PrioritySelector
        expanded={priorityDropdownExpanded}
        setExpanded={setPriorityDropdownExpanded}
        selectedPriority={selectedTicketPriority}
        setSelectedPriority={setSelectedTicketPriority}
      />
      <View style={styles.averageTime}>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{'Average'}</Text>
          {isMobile && <PercentageChange change={averagePercentageChange} />}
        </View>
        <View style={styles.averageTimeValues}>
          <View style={styles.valueContainer}>
            <Text style={styles.value}>
              {Number.isFinite(averageHours) ? pad(averageHours) : '-'}
              {':'}
              {Number.isFinite(averageMinutes) ? pad(averageMinutes) : '-'}
            </Text>
            {!isMobile && <PercentageChange change={averagePercentageChange} />}
          </View>
          {comparisonEnabled && <Text style={[styles.value, styles.valueCompared]}>
            {Number.isFinite(averageHoursCompared) ? pad(averageHoursCompared) : '-'}
            {':'}
            {Number.isFinite(averageMinutesCompared) ? pad(averageMinutesCompared) : '-'}
          </Text>}
        </View>
      </View>
      <View style={styles.fastestSlowestTimes}>
        <View style={{ flex: 1 }}>
          <View style={styles.titleContainer}>
            <Text style={[styles.title, styles.fastestTitle]}>{'Fastest'}</Text>
            {isMobile && <PercentageChange change={fastestPercentageChange} />}
          </View>
          <View style={styles.valueContainer}>
            <Text style={styles.value}>
              {Number.isFinite(fastestHours) ? pad(fastestHours) : '-'}
              {':'}
              {Number.isFinite(fastestMinutes) ? pad(fastestMinutes) : '-'}
            </Text>
            {!isMobile && <PercentageChange change={fastestPercentageChange} />}
          </View>
          {comparisonEnabled && <Text style={[styles.value, styles.valueCompared]}>
            {Number.isFinite(fastestHoursCompared) ? pad(fastestHoursCompared) : '-'}
            {':'}
            {Number.isFinite(fastestMinutesCompared) ? pad(fastestMinutesCompared) : '-'}
          </Text>}
        </View>
        <View style={styles.divider} />
        <View style={{ flex: 1 }}>
          <View style={styles.titleContainer}>
            <Text style={[styles.title, styles.slowestTitle]}>{'Slowest'}</Text>
            {isMobile && <PercentageChange change={slowestPercentageChange} />}
          </View>
          <View style={styles.valueContainer}>
            <Text style={styles.value}>
              {Number.isFinite(slowestHours) ? pad(slowestHours) : '-'}
              {':'}
              {Number.isFinite(slowestMinutes) ? pad(slowestMinutes) : '-'}
            </Text>
            {!isMobile && <PercentageChange change={slowestPercentageChange} />}
          </View>
          {comparisonEnabled && <Text style={[styles.value, styles.valueCompared]}>
            {Number.isFinite(slowestHoursCompared) ? pad(slowestHoursCompared) : '-'}
            {':'}
            {Number.isFinite(slowestMinutesCompared) ? pad(slowestMinutesCompared) : '-'}
          </Text>}
        </View>
      </View>
    </ViewShot>
  </View>;
};

const styles = StyleSheet.create({
  content: {
    ...Style.row,
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    flex: 2.4,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 14,
    lineHeight: 22,
    letterSpacing: 0,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  value: {
    fontSize: 30,
    lineHeight: isMobile ? undefined : 30,
    letterSpacing: 0,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginEnd: 8,
  },
  valueCompared: {
    color: Colors.light.hereworksYellow600,
    marginTop: isMobile ? undefined : 4,
  },
  averageTime: {
    flex: 1,
    alignSelf: 'stretch',
    paddingBottom: isMobile ? 6 : undefined,
    marginBottom: isMobile ? 12 : undefined,
    borderBottomWidth: isMobile ? 1 : undefined,
    borderBottomColor: Colors.light.hereworksWhite500,
  },
  averageTimeValues: {
    flexDirection: isMobile ? 'row' : 'column',
  },
  percentageChange: {
    ...Style.row,
    marginStart: 4,
  },
  fastestSlowestTimes: {
    ...Style.row,
    flex: 2,
  },
  divider: {
    width: 1,
    backgroundColor: Colors.light.hereworksWhite500,
    height: '100%',
    marginHorizontal: 16,
  },
  fastestTitle: {
    color: Colors.light.hereworksGreen500,
  },
  slowestTitle: {
    color: Colors.light.hereworksRed700,
  },
});

export default TimesOnTask;
